'use client';

import { useState } from 'react';
import { helpFeedback } from '@/lib/analytics';

interface HelpfulFeedbackProps {
  language: 'en' | 'zh';
  articleSlug?: string;
}

type FeedbackType = 'happy' | 'neutral' | 'sad' | null;

export default function HelpfulFeedback({
  language,
  articleSlug,
}: HelpfulFeedbackProps) {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleFeedback = (feedback: FeedbackType) => {
    setSelectedFeedback(feedback);
    setSubmitted(true);
    if (feedback) helpFeedback(articleSlug, feedback, language);
  };

  const content = {
    en: {
      title: 'Was this article helpful?',
      thankYou: 'Thank you for your feedback!',
    },
    zh: {
      title: '這篇文章對您有幫助嗎？',
      thankYou: '感謝您的回饋！',
    },
  };

  const t = content[language];

  return (
    <div className="text-center py-8">
      <h3
        className="text-xl font-semibold mb-6"
        style={{ color: 'var(--color-foreground)' }}
      >
        {t.title}
      </h3>

      {!submitted ? (
        <div className="flex justify-center gap-6">
          <button
            onClick={() => handleFeedback('happy')}
            className="group flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:bg-green-50 dark:hover:bg-green-900/20 focus:outline-none focus:ring-2 focus:ring-green-500"
            aria-label="Helpful"
          >
            <span className="text-5xl transition-transform group-hover:scale-110">
              😊
            </span>
          </button>

          <button
            onClick={() => handleFeedback('neutral')}
            className="group flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:bg-yellow-50 dark:hover:bg-yellow-900/20 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Somewhat helpful"
          >
            <span className="text-5xl transition-transform group-hover:scale-110">
              😐
            </span>
          </button>

          <button
            onClick={() => handleFeedback('sad')}
            className="group flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Not helpful"
          >
            <span className="text-5xl transition-transform group-hover:scale-110">
              😞
            </span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <span className="text-5xl">
            {selectedFeedback === 'happy' && '😊'}
            {selectedFeedback === 'neutral' && '😐'}
            {selectedFeedback === 'sad' && '😞'}
          </span>
          <p
            className="text-lg"
            style={{ color: 'var(--color-foreground-secondary)' }}
          >
            {t.thankYou}
          </p>
        </div>
      )}
    </div>
  );
}
